import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import RateLockBadge from "@/components/RateLockBadge";
import {
  Mail,
  CheckCircle2,
  Clock,
  XCircle,
  DollarSign,
  Copy,
  Check,
  ChevronRight,
  Search,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  submitted: { label: "Submitted", short: "Received", tone: "ink" },
  under_review: { label: "Under review", short: "Under review", tone: "amber" },
  approved: { label: "Approved", short: "Approved", tone: "emerald" },
  rejected: { label: "Rejected", short: "Rejected", tone: "red" },
  paid: { label: "Paid", short: "Paid", tone: "emerald" },
};

const STEPS = [
  { key: "submitted", label: "Received", Icon: Mail },
  { key: "under_review", label: "Under review", Icon: Clock },
  { key: "approved", label: "Approved", Icon: CheckCircle2 },
  { key: "paid", label: "Paid", Icon: DollarSign },
];

const TONE_BG = {
  ink: "bg-[#0b0d17] text-white",
  emerald: "bg-[#346af8] text-white",
  amber: "bg-amber-500 text-white",
  red: "bg-red-500 text-white",
};

const TONE_CHIP = {
  ink: "bg-[#0b0d17]/8 text-[#0b0d17] border-[#0b0d17]/15",
  emerald: "bg-[#346af8]/8 text-[#346af8] border-[#346af8]/20",
  amber: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  red: "bg-red-500/10 text-red-700 border-red-500/30",
};

function elapsedString(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h ago`;
}

function formatDateET(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }) + " ET";
}

export default function TrackPage() {
  const [params] = useSearchParams();
  const [reference, setReference] = useState(params.get("ref") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [stage, setStage] = useState("request");
  const [submission, setSubmission] = useState(null);
  const [otherSubmissions, setOtherSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [autoLookupTried, setAutoLookupTried] = useState(false);

  const doLookup = useCallback(async (ref, eml, { silent } = {}) => {
    if (!ref && !eml) {
      if (!silent) toast.error("Enter your reference number or email");
      return false;
    }
    setLoading(true);
    try {
      const r = await api.post("/track/lookup", {
        reference: ref || undefined,
        email: eml || undefined,
      });
      setSubmission(r.data.submission);
      setOtherSubmissions(r.data.other_submissions || []);
      setStage("view");
      // Persist for next visit — eml may be empty if user only typed a ref
      // (we cache it from the response so we can pre-fill both later).
      try {
        localStorage.setItem(
          "gc_track_recent",
          JSON.stringify({
            reference: r.data.submission?.reference || ref || "",
            email: eml || "",
            ts: Date.now(),
          }),
        );
      } catch {
        /* ignore quota / private-mode errors */
      }
      return true;
    } catch (err) {
      if (!silent) {
        toast.error(err.response?.data?.detail || "Couldn't find that submission");
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-fill from query params or last-used cache on mount, and instantly
  // open the customer's last order if we have something cached.
  useEffect(() => {
    if (autoLookupTried) return;
    setAutoLookupTried(true);
    const queryRef = (params.get("ref") || "").trim().toUpperCase();
    const queryEmail = (params.get("email") || "").trim().toLowerCase();
    if (queryRef || queryEmail) {
      // Query params win — looks deliberate (e.g. email link).
      doLookup(queryRef, queryEmail, { silent: true });
      return;
    }
    let cached = null;
    try {
      cached = JSON.parse(localStorage.getItem("gc_track_recent") || "null");
    } catch {
      cached = null;
    }
    if (!cached) return;
    // Expire cache after 30 days
    if (cached.ts && Date.now() - cached.ts > 30 * 24 * 60 * 60 * 1000) {
      localStorage.removeItem("gc_track_recent");
      return;
    }
    if (cached.reference) setReference(cached.reference);
    if (cached.email) setEmail(cached.email);
    if (cached.reference || cached.email) {
      doLookup(cached.reference || "", cached.email || "", { silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lookup = async (e) => {
    e?.preventDefault();
    const ref = reference.trim().toUpperCase();
    const eml = email.trim().toLowerCase();
    await doLookup(ref, eml);
  };

  const clearAndStartOver = () => {
    try { localStorage.removeItem("gc_track_recent"); } catch { /* ignore */ }
    setReference("");
    setEmail("");
    setSubmission(null);
    setOtherSubmissions([]);
    setStage("request");
  };

  const openOtherSubmission = async (otherRef) => {
    setLoading(true);
    try {
      const r = await api.post("/track/lookup", { reference: otherRef });
      setSubmission(r.data.submission);
      setOtherSubmissions(
        (otherSubmissions || [])
          .filter((s) => s.reference !== otherRef)
          .concat(
            submission
              ? [{
                  reference: submission.reference,
                  status: submission.status,
                  created_at: submission.created_at,
                }]
              : []
          ),
      );
      try {
        localStorage.setItem(
          "gc_track_recent",
          JSON.stringify({
            reference: r.data.submission?.reference || otherRef,
            email: email.trim().toLowerCase() || "",
            ts: Date.now(),
          }),
        );
      } catch { /* ignore */ }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't open that submission");
    } finally {
      setLoading(false);
    }
  };

  const copyRef = async () => {
    if (!submission?.reference) return;
    try {
      await navigator.clipboard.writeText(submission.reference);
    } catch { /* clipboard unavailable in this context */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      <SEO title="Track your order" />

      {/* Toptech theme hero */}
      <section className="search-home-hero-section" data-testid="track-hero" style={{ paddingBottom: 40 }}>
        <div className="main-container w-container">
          <div className="search-heading" style={{ textAlign: "center", maxWidth: 720, margin: "0 auto" }}>
            <div style={{ display: "inline-block", padding: "6px 14px", borderRadius: 90, background: "#eaf0ff", color: "#346af8", fontSize: 12, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 18 }}>
              Track order
            </div>
            <h1 className="h1 _700-bold">Where&apos;s <span style={{ color: "#346af8" }}>my payout?</span></h1>
            <p className="main-body-text _500-medium" style={{ marginTop: 18, color: "#4a4d5f" }}>
              Enter your reference number or your email — either one is enough to pull up every step of your submission.
            </p>
          </div>
        </div>
      </section>
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-20" style={{ paddingTop: 0 }}>

        {stage === "request" && (
          <form
            onSubmit={lookup}
            className="bg-white border border-[#e8e8ef] rounded-2xl p-6 md:p-7 soft-shadow space-y-5"
            data-testid="track-request-form"
          >
            <Field label="Reference number" hint="Starts with SPARK-. Find it in your confirmation email. Hyphens and spaces are forgiven.">
              <input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                data-testid="track-reference"
                placeholder="SPARK-XXXXXX-XXXX"
                className="inp font-mono-display uppercase"
                autoComplete="off"
                spellCheck={false}
              />
            </Field>
            <div className="flex items-center gap-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-[#9a93a6]">
              <div className="flex-1 h-px bg-[#e8e8ef]" />
              OR
              <div className="flex-1 h-px bg-[#e8e8ef]" />
            </div>
            <Field label="Email" hint="The address you used when submitting the card.">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="track-email"
                placeholder="you@example.com"
                className="inp"
                autoComplete="email"
              />
            </Field>
            <button
              type="submit"
              disabled={loading}
              data-testid="track-request-btn"
              className="w-full h-12 rounded-lg bg-[#346af8] text-white font-semibold hover:bg-[#2554d6] disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
            >
              {loading ? "Looking up…" : (<><Search className="w-4 h-4" /> Check status</>)}
            </button>
            <p className="text-xs text-[#9a93a6] inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Your status is never indexable — only people with the reference or email can see it.
            </p>
            <p className="text-xs text-[#9a93a6] pt-1 border-t border-[#e8e8ef]">
              Still can&apos;t find your submission?{" "}
              <a
                href="mailto:support@gcspark.com?subject=Can%27t%20track%20my%20submission&body=Hi%20team%2C%0A%0AI%20can%27t%20track%20my%20submission.%20Details%3A%0A%0AReference%20I%20tried%3A%20%0AEmail%20I%20used%20at%20submission%3A%20%0AApprox.%20submission%20date%3A%20%0A%0AThanks."
                data-testid="track-help-mailto"
                className="font-semibold text-[#346af8] hover:underline"
              >
                Email us at support@gcspark.com
              </a>{" "}
              and we&apos;ll dig it out for you.
            </p>
          </form>
        )}

        {stage === "view" && submission && (
          <div className="space-y-5" data-testid="track-result">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-[#9a93a6]">
                Saved to this device — opens instantly next time.
              </span>
              <button
                type="button"
                onClick={clearAndStartOver}
                data-testid="track-clear-cache-btn"
                className="text-xs font-semibold text-[#346af8] hover:underline decoration-dotted underline-offset-4"
              >
                Look up a different order →
              </button>
            </div>
            {/* Status Hero */}
            <div className="bg-white border border-[#e8e8ef] rounded-2xl p-6 md:p-7 soft-shadow">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="text-xs font-semibold tracking-wider uppercase text-[#9a93a6] mb-1.5">
                    Reference
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono-display font-bold text-xl md:text-2xl text-[#0b0d17] break-all">
                      {submission.reference}
                    </span>
                    <button
                      type="button"
                      onClick={copyRef}
                      data-testid="track-copy-ref"
                      className="shrink-0 h-8 w-8 rounded-md border border-[#e8e8ef] text-[#6c6376] hover:bg-[#f4f4f8] flex items-center justify-center transition-colors"
                      aria-label="Copy reference"
                    >
                      {copied ? <Check className="w-4 h-4 text-[#346af8]" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
                <StatusBadge status={submission.status} />
              </div>
              {submission.rate_locked_until && (
                <div className="mt-3">
                  <RateLockBadge until={submission.rate_locked_until} />
                </div>
              )}

              <div className="mt-6 grid grid-cols-2 gap-3">
                <Stat
                  label="Time elapsed"
                  value={elapsedString(submission.created_at)}
                />
                <Stat
                  label="Typical processing"
                  value="Under 8 hours"
                  hint="Most submissions clear within this window."
                />
              </div>
              {otherSubmissions && otherSubmissions.length > 0 && (
                <div className="mt-6 pt-5 border-t border-[#e8e8ef]" data-testid="track-other-submissions">
                  <div className="text-xs font-semibold tracking-wider uppercase text-[#9a93a6] mb-2">
                    Your other orders ({otherSubmissions.length})
                  </div>
                  <div className="space-y-1.5">
                    {otherSubmissions.map((os) => (
                      <button
                        key={os.reference}
                        type="button"
                        onClick={() => openOtherSubmission(os.reference)}
                        data-testid={`track-other-${os.reference}`}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-[#e8e8ef] hover:bg-[#f4f4f8] transition-colors text-left"
                      >
                        <span className="font-mono-display text-sm text-[#0b0d17] truncate">
                          {os.reference}
                        </span>
                        <span className="flex items-center gap-2 text-xs text-[#6c6376] flex-shrink-0">
                          <StatusBadge status={os.status} compact />
                          <ChevronRight className="w-3.5 h-3.5" />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Progress timeline */}
            <div className="bg-white border border-[#e8e8ef] rounded-2xl p-6 md:p-7 soft-shadow">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-display text-lg font-semibold text-[#0b0d17]">Progress</h2>
                {submission.timeline?.length > 0 && (
                  <span className="text-xs text-[#9a93a6]">
                    {submission.timeline.length} update{submission.timeline.length === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              <Timeline submission={submission} />
            </div>

            {/* Submission details */}
            <div className="bg-white border border-[#e8e8ef] rounded-2xl p-6 md:p-7 soft-shadow">
              <div className="flex items-baseline justify-between mb-5">
                <h2 className="font-display text-lg font-semibold text-[#0b0d17]">Submission details</h2>
                <div className="text-right">
                  <div className="text-[10px] font-semibold tracking-wider uppercase text-[#9a93a6]">
                    Estimated payout
                  </div>
                  <div className="font-display text-2xl font-bold text-[#346af8]">
                    ${(submission.estimated_payout || 0).toFixed(2)}
                  </div>
                </div>
              </div>

              <dl className="divide-y divide-[#e8e8ef]">
                <DL label="Customer" value={submission.full_name || "—"} />
                <DL label="Submitted" value={formatDateET(submission.created_at)} />
                <DL label="Last updated" value={
                  submission.updated_at && submission.updated_at !== submission.created_at
                    ? formatDateET(submission.updated_at)
                    : "—"
                } />
                <DL label="Payment method" value={(submission.payout_method || "—").toUpperCase()} />
                {submission.final_payout != null && (
                  <DL label="Final payout" value={`$${submission.final_payout.toFixed(2)}`} emphasize />
                )}
              </dl>
            </div>

            {/* Cards */}
            <div className="bg-white border border-[#e8e8ef] rounded-2xl p-6 md:p-7 soft-shadow">
              <div className="flex items-baseline justify-between mb-5">
                <h2 className="font-display text-lg font-semibold text-[#0b0d17]">
                  Card{submission.cards.length === 1 ? "" : "s"} on this submission
                </h2>
                <div className="text-right">
                  <div className="text-[10px] font-semibold tracking-wider uppercase text-[#9a93a6]">
                    Total value
                  </div>
                  <div className="font-display text-xl font-bold text-[#0b0d17]">
                    ${submission.cards.reduce((s, c) => s + (c.balance || 0), 0).toFixed(2)}
                  </div>
                </div>
              </div>

              <ul className="space-y-3">
                {submission.cards.map((c, i) => (
                  <li
                    key={i}
                    data-testid={`track-card-${i}`}
                    className="border border-[#e8e8ef] rounded-xl p-4 bg-[#f4f4f8]"
                  >
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="shrink-0 inline-flex h-6 px-2 items-center rounded-full bg-[#0b0d17] text-white text-[10px] font-semibold tracking-wider uppercase">
                          Card {i + 1}
                        </span>
                        <span className="font-display font-semibold text-[#0b0d17] truncate">
                          {c.brand_name}
                        </span>
                      </div>
                      <span className="font-display font-bold text-[#0b0d17]">
                        ${(c.balance || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#6c6376]">
                      {c.condition && (
                        <span><span className="text-[#9a93a6]">Condition:</span> <strong className="text-[#0b0d17] capitalize">{c.condition}</strong></span>
                      )}
                      {c.card_type && (
                        <span><span className="text-[#9a93a6]">Type:</span> <strong className="text-[#0b0d17] capitalize">{c.card_type}</strong></span>
                      )}
                      {c.has_receipt !== undefined && (
                        <span><span className="text-[#9a93a6]">Receipt:</span> <strong className="text-[#0b0d17]">{c.has_receipt ? "Yes" : "No"}</strong></span>
                      )}
                      {c.card_number_masked && (
                        <span><span className="text-[#9a93a6]">Card #:</span> <strong className="font-mono-display text-[#0b0d17]">{c.card_number_masked}</strong></span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center justify-between text-xs text-[#9a93a6] px-1">
              <button
                type="button"
                onClick={() => { setStage("request"); setSubmission(null); }}
                data-testid="track-back-btn"
                className="inline-flex items-center gap-1 hover:text-[#0b0d17] transition-colors"
              >
                ← Check a different submission
              </button>
              <a href="mailto:support@gcspark.com" className="inline-flex items-center gap-1 hover:text-[#0b0d17] transition-colors">
                Something off? Email operations <ChevronRight className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}

        <style>{`
          .inp {
            width: 100%;
            height: 48px;
            padding: 0 16px;
            border-radius: 8px;
            border: 1px solid #e8e8ef;
            background: white;
            color: #0b0d17;
            outline: none;
            transition: box-shadow .15s, border-color .15s;
          }
          .inp:focus { box-shadow: 0 0 0 2px #346af8; border-color: transparent; }
        `}</style>
      </section>
    </>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wider text-[#6c6376] mb-1.5">
        {label}
      </span>
      {children}
      {hint && <span className="block text-xs text-[#9a93a6] mt-1.5">{hint}</span>}
    </label>
  );
}

function StatusBadge({ status, compact }) {
  const m = STATUS_META[status] || STATUS_META.submitted;
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center gap-1.5 ${compact ? "px-2 h-6 text-[11px]" : "px-3 h-8 text-xs"} rounded-full font-semibold border ${TONE_CHIP[m.tone]}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${m.tone === "amber" ? "bg-amber-500" : m.tone === "red" ? "bg-red-500" : m.tone === "emerald" ? "bg-[#346af8]" : "bg-[#0b0d17]"} ${status === "under_review" ? "animate-pulse" : ""}`} />
      {compact ? m.short : m.label}
    </span>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="rounded-xl border border-[#e8e8ef] bg-[#f4f4f8] p-3.5">
      <div className="text-[10px] font-semibold tracking-wider uppercase text-[#9a93a6]">
        {label}
      </div>
      <div className="font-display font-bold text-[#0b0d17] mt-0.5 text-base md:text-lg">
        {value}
      </div>
      {hint && <div className="text-[11px] text-[#9a93a6] mt-0.5">{hint}</div>}
    </div>
  );
}

function Timeline({ submission }) {
  // build a map of status -> first matching log timestamp, if any
  const logsByStatus = {};
  (submission.timeline || []).forEach((t) => {
    if (!logsByStatus[t.status]) logsByStatus[t.status] = t.timestamp;
  });
  const currentIdx = STEPS.findIndex((s) => s.key === submission.status);
  const rejected = submission.status === "rejected";

  return (
    <ol className="relative pl-7">
      {STEPS.map((step, i) => {
        const past = !rejected && currentIdx > i;
        const current = !rejected && currentIdx === i;
        const reached = past || current;
        const ts = logsByStatus[step.key] || (step.key === "submitted" ? submission.created_at : null);
        const meta = STATUS_META[step.key];
        const sub = current ? "In progress" : past ? "Complete" : "Pending";
        const tone = current ? meta.tone : past ? "emerald" : "ink";
        return (
          <li key={step.key} className="relative pb-6 last:pb-0">
            {i < STEPS.length - 1 && (
              <span
                className={`absolute left-[-14px] top-7 bottom-0 w-px ${past ? "bg-[#346af8]" : "bg-[#e8e8ef]"}`}
              />
            )}
            <span
              className={`absolute -left-[26px] w-7 h-7 rounded-full flex items-center justify-center ${
                reached ? TONE_BG[tone] : "bg-white border border-[#e8e8ef] text-[#9a93a6]"
              }`}
            >
              <step.Icon className="w-3.5 h-3.5" />
            </span>
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <div>
                <div className={`text-sm font-semibold ${reached ? "text-[#0b0d17]" : "text-[#9a93a6]"}`}>
                  {step.label}
                </div>
                <div className={`text-xs mt-0.5 ${
                  current ? "text-[#346af8]" : past ? "text-[#346af8]" : "text-[#9a93a6]"
                }`}>
                  {sub}
                </div>
              </div>
              {ts && reached && (
                <span className="text-[11px] text-[#9a93a6] font-mono-display">
                  {new Date(ts).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true })} ET
                </span>
              )}
            </div>
          </li>
        );
      })}
      {rejected && (
        <li className="relative">
          <span className="absolute -left-[26px] w-7 h-7 rounded-full flex items-center justify-center bg-red-500 text-white">
            <XCircle className="w-3.5 h-3.5" />
          </span>
          <div className="text-sm font-semibold text-red-600">Rejected</div>
          <div className="text-xs mt-0.5 text-[#9a93a6]">Check your email for details.</div>
        </li>
      )}
    </ol>
  );
}

function DL({ label, value, emphasize }) {
  return (
    <div className="flex items-baseline justify-between py-2.5 text-sm">
      <dt className="text-[#9a93a6]">{label}</dt>
      <dd className={`text-right ${emphasize ? "text-[#346af8] font-bold text-base" : "text-[#0b0d17] font-medium"}`}>
        {value}
      </dd>
    </div>
  );
}
